import { BUSINESS_TIMEZONE } from '@omecare/utils';
import { intlFormat } from 'date-fns/intlFormat';
import { useMemo, useState } from 'react';
import { GenerateOfficeDoctorAttendingAccountingResponse } from '@/hooks/mutations/use-generate-office-doctor-attending-accounting';
import { useGetMeQuery } from '@/hooks/queries/use-me';
import { doctorFullnameTitleTrad } from '@/utils/doctor';

export const useAccoutingDoctorAttendingViewModel = ({
  accounting,
  officeName,
}: {
  accounting: GenerateOfficeDoctorAttendingAccountingResponse;
  officeName: string;
}) => {
  const [logoLoaded, setLogoLoaded] = useState<boolean>(false);

  // Guard
  const { data: me, isPending: mePending } = useGetMeQuery();

  const pageLoaded = useMemo(() => {
    const meLoaded = !!me && !mePending;

    return meLoaded;
  }, [mePending]);

  return {
    unauthorized: !mePending && !me,
    pageLoaded,
    logoLoaded,
    setLogoLoaded,
    information: {
      officeName: officeName.toUpperCase(),
      period: accounting.period,
      periodFrom: intlFormat(
        accounting.period.from,
        {
          timeZone: BUSINESS_TIMEZONE,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
        },
        {
          locale: 'fr',
        },
      ),
      periodTo: intlFormat(
        accounting.period.to,
        {
          timeZone: BUSINESS_TIMEZONE,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
        },
        {
          locale: 'fr',
        },
      ),
      date: intlFormat(
        new Date(),
        {
          timeZone: BUSINESS_TIMEZONE,
          month: 'long',
          day: '2-digit',
          year: 'numeric',
        },
        {
          locale: 'fr',
        },
      ),
      doctorAttending: {
        ...accounting.doctorAttending,
        fullname: doctorFullnameTitleTrad(accounting.doctorAttending),
      },
      acountingPeriodMonth: intlFormat(
        new Date(accounting.period.from),
        { timeZone: BUSINESS_TIMEZONE, month: 'long', year: 'numeric' },
        {
          locale: 'fr',
        },
      ).toUpperCase(),
      amountFeeCollected: accounting.amountFeeCollected,
      amountMedicalAssistantFee: accounting.amountMedicalAssistantFee,
      amountSubstituteMedicalAssistantFee:
        accounting.amountSubstituteMedicalAssistantFee,
      amountPaid: accounting.amountPaid,
      amountPayBack: accounting.amountPayBack,
      amountCommissionFee: accounting.amountCommissionFee,
      amountFeeBeforePayback:
        accounting.amountFeeCollected - accounting.amountPaid,
      amountFee: accounting.amountFee,
      amountAvailable: accounting.amountAvailable,
      attendingShare: accounting.attendingShare,
      medicalAssistantShare: accounting.medicalAssistantShare,
    },
  };
};
export type AccoutingDoctorAttendingViewModel = ReturnType<
  typeof useAccoutingDoctorAttendingViewModel
>;
